import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UrlService } from './url.service';
import { BehaviorSubject, catchError, EMPTY, firstValueFrom, Observable, throwError } from 'rxjs';
import { User } from '../models/User';
import { PageEvent } from '@angular/material/paginator';
import { PagedData } from '../models/PagedData';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly url: string;
  private readonly userRoleSubject = new BehaviorSubject<string | null>(null);
  public userRole$ = this.userRoleSubject.asObservable();

  constructor(private readonly http: HttpClient, private readonly urlService: UrlService) {
    this.url = this.urlService.generateUrl('users');
  }

  getUsers(page: PageEvent): Observable<PagedData<any>>  {
    return this.http.post<PagedData<any>>(this.url + '/GetUsers', page).pipe(
      catchError((error: any) => this.handleError(error))
    );
  }

  getUsersCompanies(userId: string): Observable<string[]> {
    const params = new HttpParams().set('userId', userId);
    return this.http.get<string[]>(this.url + '/GetUserCompanies', { params }).pipe(
      catchError((error: any) => this.handleError(error))
    );
  }

  updateUser(user: User): Observable<void> {
    return this.http.post<void>(this.url + '/UpdateUser', user).pipe(
      catchError((error: any) => this.handleError(error))
    );
  }

  createUser(user: User): Observable<void> {
    return this.http.post<void>(this.url + '/CreateUser', user).pipe(
      catchError((error: any) => this.handleError(error))
    );
  }

  getUserRole(userId: string): Observable<string> {
    return this.http.get(`${this.url}/GetUserRole?userId=${userId}`, { responseType: 'text' }).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => new Error(error.message || 'An error occurred while fetching the user role.'));
      })
    );
  }

  deleteUser(userView: any): Observable<boolean> {
    return this.http.post<boolean>(this.url + '/DeleteUser', userView).pipe(
      catchError((error: any) => this.handleError(error))
    );
  }

  async fetchUserRole(userId: string): Promise<string> {
    return firstValueFrom(this.getUserRole(userId))
      .then((role) => {
        return role;
      })
      .catch(() => {
        return '';
      });
  }

  setUserRole(role: string) {
    this.userRoleSubject.next(role);
  }

  private handleError(error: any): Observable<never> {
    return EMPTY;
  }
}
